import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { Search } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useTheme } from '@/hooks/useTheme'

type AiModeButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  loading?: boolean
}

export const AiModeButton = forwardRef<HTMLButtonElement, AiModeButtonProps>(
  ({ className, children = 'Analisar com IA', loading, disabled, ...props }, ref) => {
    const { isDark } = useTheme()

    return (
      <div
        className={cn(
          'ai-mode-btn group relative inline-flex',
          isDark && 'ai-mode-btn--dark',
          className,
        )}
      >
        <span className='ai-mode-btn-border inline-flex rounded-full p-[2px]'>
          <button
            ref={ref}
            type='button'
            disabled={disabled || loading}
            className={cn(
              'inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full px-4 py-2',
              'text-[13px] font-medium transition-[transform,box-shadow,background-color,color] duration-150',
              'active:scale-[0.98]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-55',
              isDark
                ? [
                    'bg-[#16171c] text-text-primary',
                    'shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_2px_10px_rgba(0,0,0,0.35)]',
                    'hover:bg-[#1c1d24] hover:shadow-[0_4px_18px_rgba(0,0,0,0.45)]',
                    'focus-visible:ring-offset-bg-main',
                  ]
                : [
                    'bg-white text-[#3f3f46]',
                    'shadow-[0_1px_2px_rgba(15,23,42,0.06)]',
                    'hover:shadow-[0_4px_14px_rgba(15,23,42,0.08)]',
                    'focus-visible:ring-offset-white',
                  ],
            )}
            {...props}
          >
            {loading ? (
              <span
                className={cn(
                  'h-4 w-4 animate-spin rounded-full border-2',
                  isDark
                    ? 'border-white/15 border-t-text-primary'
                    : 'border-[#3f3f46]/25 border-t-[#3f3f46]',
                )}
                aria-hidden
              />
            ) : (
              <Search
                size={17}
                strokeWidth={2.25}
                className={cn('shrink-0', isDark ? 'text-text-secondary' : 'text-[#3f3f46]')}
                aria-hidden
              />
            )}
            <span>{children}</span>
          </button>
        </span>
      </div>
    )
  },
)

AiModeButton.displayName = 'AiModeButton'
